import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Obfuscate email address - gets assembled after page load to avoid bot detection
    const emailElement = document.getElementById('privacy-email-link');
    if (emailElement) {
      const parts = ['info', '@', 'ocean', 'bio', 'metrics', '.', 'com'];
      const email = parts.join('');
      emailElement.textContent = email;
    }
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Header isMainPage={false} />
      <main className="flex-grow-1 mt-5 pt-5">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <h1 className="display-4 mb-4 text-center" style={{ color: '#00f5ff' }}>Privacy Policy</h1>

              <p className="mb-4 text-center"><strong>Effective Date:</strong> September 12, 2025</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Introduction</h2>
              <p className="mb-3">
                Ocean BioMetrics Ltd ("Ocean BioMetrics," "we," "us," or "our") is committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy explains what information we collect through our website www.oceanbiometrics.com (the "Website"), how we use and protect that information, and your rights regarding your personal data.
              </p>
              <p className="mb-4">
                By using our Website or engaging with our consulting services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree, please do not use our Website or services.
              </p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Information We Collect</h2>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>1. Information You Provide Directly</h3>
              <p className="mb-2">When you interact with our Website or engage our consulting services, you may provide us with:</p>
              <ul className="mb-3">
                <li><strong>Contact Information:</strong> When you submit our contact form or email us, we collect your name, email address, organization name, and any message content you provide.</li>
                <li><strong>Professional Information:</strong> If you engage our consulting services, we may collect additional information such as your job title, phone number, and project requirements.</li>
                <li><strong>Communication Records:</strong> We maintain records of our correspondence when you contact us for inquiries, support, or consulting engagements.</li>
              </ul>

              <h3 className="h5 mb-2" style={{ color: '#40e0d0' }}>2. Information Collected Automatically</h3>
              <p className="mb-2">When you visit our Website, we automatically collect certain technical information:</p>
              <ul className="mb-4">
                <li><strong>Usage Data:</strong> We use Google Analytics to collect anonymized information about how visitors use our Website, including pages visited, time spent on pages, and general geographic location (country/city level).</li>
                <li><strong>Cookies and Tracking:</strong> We use cookies through our cookie consent management tool (CookieYes) to enhance your browsing experience.</li>
                <li><strong>Technical Information:</strong> Your browser type, device type, IP address (anonymized), and referring website.</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>How We Use Your Information</h2>
              <p className="mb-2">We use collected information for the following purposes:</p>
              <ul className="mb-4">
                <li><strong>Service Delivery:</strong> To respond to inquiries, provide consulting services, and manage client relationships</li>
                <li><strong>Communication:</strong> To send project updates, respond to questions, and provide requested information</li>
                <li><strong>Website Improvement:</strong> To analyze Website usage and improve user experience</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
                <li><strong>Business Operations:</strong> To maintain records, manage contracts, and operate our business</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>How We Share Your Information</h2>
              <p className="mb-2">We do not sell, rent, or trade your personal information. We may share information only in these circumstances:</p>
              <ul className="mb-4">
                <li><strong>Service Providers:</strong> With trusted third parties who assist us in operating our Website and business (e.g., Netlify for form processing, Google for analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
                <li><strong>Business Protection:</strong> To protect our rights, safety, or property, or that of our clients and the public</li>
                <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
              </ul>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Data Security</h2>
              <p className="mb-2">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
              <ul className="mb-2">
                <li>HTTPS encryption for all Website communications</li>
                <li>Secure storage of contact form submissions</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Regular security reviews of our systems and processes</li>
              </ul>
              <p className="mb-4">However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Your Rights and Choices</h2>
              <p className="mb-2">Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul className="mb-2">
                <li><strong>Access:</strong> Request information about what personal data we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications (though we primarily send transactional communications)</li>
                <li><strong>Cookie Management:</strong> Control cookies through your browser settings or our cookie consent tool</li>
              </ul>
              <p className="mb-4">To exercise these rights, please contact us at info@oceanbiometrics.com.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Data Retention</h2>
              <p className="mb-4">We retain personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law or for legitimate business purposes such as maintaining records of completed projects.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>International Data Transfers</h2>
              <p className="mb-4">We are based in Ireland, and your information may be processed and stored in Ireland or other countries where we or our service providers operate. By using our Website or services, you consent to the transfer of your information to these locations.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Children's Privacy</h2>
              <p className="mb-4">Our Website and services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children under 18.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Third-Party Links</h2>
              <p className="mb-4">Our Website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Updates to This Policy</h2>
              <p className="mb-4">We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" above.</p>

              <h2 className="h3 mb-3" style={{ color: '#00f5ff' }}>Contact Us</h2>
              <p className="mb-2">If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
              <p className="mb-1"><strong>Email:</strong> <button id="privacy-email-link" className="text-decoration-none hover-primary border-0 bg-transparent p-0" style={{ cursor: 'pointer', color: 'inherit' }} onClick={() => { const parts = ['info', '@', 'ocean', 'bio', 'metrics', '.', 'com']; const email = parts.join(''); window.location.href = `mailto:${email}`; }}>Loading...</button></p>
              <p className="mb-1"><strong>Website:</strong> www.oceanbiometrics.com</p>
              <p className="mb-0">Ocean BioMetrics Ltd<br /></p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;